import { createSelector } from '@reduxjs/toolkit'

import {
    INCOME_PLATFORM_DATA_STATUS_IN_PROGRESS,
    INCOME_PLATFORM_DATA_STATUS_READY,
    INCOME_PLATFORM_STATUS_AVAILABLE,
    INCOME_PLATFORM_STATUS_CONNECTED,
} from '@/constants'

import iconAmazonFlex from '@/assets/svg/rollee/icon-amazonflex.svg'
import iconBolt from '@/assets/svg/rollee/icon-bolt.svg'
import iconDeliveroo from '@/assets/svg/rollee/icon-deliveroo.svg'
import iconFreeNow from '@/assets/svg/rollee/icon-freenow.svg'
import iconHungryPanda from '@/assets/svg/rollee/icon-hungrypanda.svg'
import iconJustEat from '@/assets/svg/rollee/icon-justeat.svg'
import iconStuart from '@/assets/svg/rollee/icon-stuart.svg'
import iconUber from '@/assets/svg/rollee/icon-uber.svg'
import iconUberEats from '@/assets/svg/rollee/icon-ubereats.svg'

const PLATFORM_BOLT = 'bolt'
const PLATFORM_DELIVEROO = 'deliveroo'
const PLATFORM_FREENOW = 'freenow'
const PLATFORM_HUNGRYPANDA = 'hungrypanda'
const PLATFORM_JUST_EAT = 'justeat'
const PLATFORM_STUART = 'stuart'
const PLATFORM_UBER = 'uber'
const PLATFORM_UBER_EATS = 'ubereats'
const PLATFORM_AMAZONFLEX = 'amazonflex'

const PlatformIcons = {
    [PLATFORM_UBER]: iconUber,
    [PLATFORM_JUST_EAT]: iconJustEat,
    [PLATFORM_DELIVEROO]: iconDeliveroo,
    [PLATFORM_UBER_EATS]: iconUberEats,
    [PLATFORM_BOLT]: iconBolt,
    [PLATFORM_STUART]: iconStuart,
    [PLATFORM_FREENOW]: iconFreeNow,
    [PLATFORM_HUNGRYPANDA]: iconHungryPanda,
    [PLATFORM_AMAZONFLEX]: iconAmazonFlex,
}

const getPlatformIconUrl = (name) => PlatformIcons[name] ?? ''

const getIncomePlatformsSlice = (state) => state.incomePlatforms
const getIncomePlatformsItems = (state) => getIncomePlatformsSlice(state).items || []

const getIncomePlatformsEnhancedItems = createSelector([getIncomePlatformsItems], (items) =>
    items.map((item) => ({
        ...item,
        icon: getPlatformIconUrl(item.slug),
    }))
)

const getIncomePlatformsReady = createSelector([getIncomePlatformsEnhancedItems], (items) =>
    items.filter(
        ({ connectionStatus, connection }) =>
            connectionStatus === INCOME_PLATFORM_STATUS_CONNECTED &&
            connection?.dataStatus === INCOME_PLATFORM_DATA_STATUS_READY
    )
)

const getHasUnconfirmedIncomePlatforms = createSelector(
    [getIncomePlatformsReady],
    (items) => items.filter(({ connection }) => connection?.data?.confirmedByUser === null).length > 0
)

const getIncomePlatformsInProgress = createSelector([getIncomePlatformsEnhancedItems], (items) =>
    items.filter(
        ({ connectionStatus, connection }) =>
            connectionStatus === INCOME_PLATFORM_STATUS_CONNECTED &&
            connection?.dataStatus === INCOME_PLATFORM_DATA_STATUS_IN_PROGRESS
    )
)

const getIncomePlatformsAvailable = createSelector([getIncomePlatformsEnhancedItems], (items) =>
    items.filter(({ connectionStatus }) => connectionStatus === INCOME_PLATFORM_STATUS_AVAILABLE)
)

export const getIncomePlatforms = createSelector(
    [getIncomePlatformsSlice, getIncomePlatformsAvailable, getIncomePlatformsInProgress, getIncomePlatformsReady],
    (incomePlatforms, itemsAvailable, itemsInProgress, itemsReady) => {
        const { shouldFetch } = incomePlatforms

        return {
            itemsAvailable,
            itemsReady,
            itemsInProgress,
            shouldFetch,
        }
    }
)

export const getHasUnfinishedIncomePlatforms = createSelector(
    [getHasUnconfirmedIncomePlatforms, getIncomePlatformsInProgress],
    (itemsUnconfirmed, itemsInProgress) => itemsUnconfirmed || itemsInProgress.length > 0
)
